<form [formGroup]="form">
  <div
    class="mt-6"
    e2e-render-type="listbox-input"
    e2e-question-code="street"
    [hidden]="withPoBox"
    [ngClass]="{ 'g-control-invalid group': isValidationShouldBeShown(this.form.controls.street) }"
  >
    <label id="street-label" for="street" cslLabel required class="mx-2 mb-1"> Street Address </label>
    @let streets = streets$ | async;
    <csl-autocomplete
      id="street"
      placeholder="Enter street address"
      formControlName="street"
      [attr.aria-required]="true"
      [attr.aria-labelledby]="'street-label'"
      aria-placeholder="Start typing your street address to find matching addresses"
      [ariaDescription]="
        'Start typing at least ' +
        streetMinInput +
        ' characters to see matching addresses. Select a match when suggestions appear.'
      "
      [maxLength]="100"
      [selectionRequired]="true"
      [isLoading]="isStreetLoading"
      [displayFn]="streetDisplayFn"
      (inputChange)="streetInput$.next($event)"
      [attr.aria-invalid]="isValidationShouldBeShown(this.form.controls.street) ? 'true' : null"
      [attr.aria-describedby]="isValidationShouldBeShown(this.form.controls.street) ? errorIds.street : null"
      [attr.aria-required]="true"
    >
      @let streetInput = streetInput$ | async;
      @if (streetInput && streetInput.length < streetMinInput) {
        <div class="text-gray-750 p-6 text-center text-sm">Please enter at least {{ streetMinInput }} symbols</div>
      } @else if (streetInput && !isStreetLoading && (!streets || !streets.length)) {
        <div class="text-gray-750 p-6 text-center text-sm" data-testid="autocomplete-no-results">
          Sorry, we couldn’t find matching results
        </div>
      }
      @for (item of streets; track $index) {
        <div [cslOption]="item" [innerHTML]="suggestionDisplayFn(item) | bold: streetInput"></div>
      }
    </csl-autocomplete>
    @if (isValidationShouldBeShown(this.form.controls.street)) {
      <csl-validation-messages
        class="ml-1"
        [errors]="this.form.controls.street.errors"
        [attr.id]="errorIds.street"
        aria-live="polite"
      >
        <csl-validation-message name="required-address">
          {{ errorMessages.street }}
        </csl-validation-message>
      </csl-validation-messages>
    }
  </div>

  <div class="mt-6" [hidden]="withPoBox" e2e-render-type="text-input" e2e-question-code="street2">
    <label for="street2" cslLabel class="mx-2 mb-1">Street Address 2</label>
    <input
      id="street2"
      cslInput
      formControlName="street2"
      maxlength="100"
      placeholder="Address details (unit, apt., suite)"
    />
  </div>

  <div
    class="mt-6"
    [hidden]="!withPoBox"
    [ngClass]="{ 'g-control-invalid group': isValidationShouldBeShown(this.form.controls.poBox) }"
    e2e-render-type="text-input"
    e2e-question-code="poBoxNumber"
  >
    <label for="poBox" required cslLabel class="mx-2 mb-1">PO Box Address</label>
    <input
      id="poBox"
      cslInput
      formControlName="poBox"
      maxlength="45"
      placeholder="Enter PO Box Address"
      [attr.aria-invalid]="isValidationShouldBeShown(this.form.controls.poBox) ? 'true' : null"
      [attr.aria-describedby]="isValidationShouldBeShown(this.form.controls.poBox) ? errorIds.poBox : null"
    />
    @if (isValidationShouldBeShown(this.form.controls.poBox)) {
      <csl-validation-messages
        class="ml-1"
        [errors]="this.form.controls.poBox.errors"
        [attr.id]="errorIds.poBox"
        aria-live="polite"
      >
        <csl-validation-message name="required"> {{ errorMessages.poBox }} </csl-validation-message>
      </csl-validation-messages>
    }
  </div>

  <div
    class="mt-6"
    [ngClass]="{ 'g-control-invalid group': isValidationShouldBeShown(this.form.controls.city) }"
    e2e-render-type="listbox-input"
    e2e-question-code="city"
  >
    <label id="city-label" for="city" cslLabel required class="mx-2 mb-1">City</label>
    <csl-autocomplete
      id="city"
      placeholder="Enter city"
      formControlName="city"
      [attr.aria-labelledby]="'city-label'"
      [ariaDescription]="
        'Start typing at least ' +
        cityMinInput +
        ' characters to see matching cities. Select a match when suggestions appear.'
      "
      [maxLength]="50"
      [selectionRequired]="true"
      [isLoading]="isCityLoading"
      [displayFn]="cityDisplayFn"
      (inputChange)="cityInput$.next($event)"
      [attr.aria-invalid]="isValidationShouldBeShown(this.form.controls.city) ? 'true' : null"
      [attr.aria-describedby]="isValidationShouldBeShown(this.form.controls.city) ? errorIds.city : null"
      [attr.aria-required]="true"
    >
      @let cityInput = cityInput$ | async;
      @let cities = cities$ | async;
      @if (cityInput && cityInput.length < cityMinInput) {
        <div class="text-gray-750 p-6 text-center text-sm">Please enter at least {{ cityMinInput }} symbols</div>
      } @else if (cityInput && !isCityLoading && (!cities || !cities.length)) {
        <div class="text-gray-750 p-6 text-center text-sm" data-testid="autocomplete-no-results">
          Sorry, we couldn’t find matching results
        </div>
      }
      @for (item of cities; track $index) {
        <div [cslOption]="item" [innerHTML]="suggestionDisplayFn(item) | bold: cityInput"></div>
      }
    </csl-autocomplete>
    @if (isValidationShouldBeShown(this.form.controls.city)) {
      <csl-validation-messages
        class="ml-1"
        [errors]="this.form.controls.city.errors"
        [attr.id]="errorIds.city"
        aria-live="polite"
      >
        <csl-validation-message name="required-address"> {{ errorMessages.city }} </csl-validation-message>
      </csl-validation-messages>
    }
  </div>

  <div class="mt-6 flex flex-col gap-6 md:flex-row">
    <div
      class="basis-5/12"
      [ngClass]="{ 'g-control-invalid group': isValidationShouldBeShown(this.form.controls.zip) }"
      e2e-render-type="listbox-input"
      e2e-question-code="zipCode"
    >
      <label id="zip-label" for="zip" cslLabel required class="mx-2 mb-1">Zip Code</label>
      <csl-autocomplete
        id="zip"
        placeholder="Enter zip code"
        formControlName="zip"
        [attr.aria-labelledby]="'zip-label'"
        [ariaDescription]="
          'Start typing at least ' +
          zipMinInput +
          ' characters to see matching zip codes. Select a match when suggestions appear.'
        "
        [maxLength]="10"
        [isLoading]="isZipLoading"
        [displayFn]="zipDisplayFn"
        [selectionRequired]="true"
        [inputPattern]="zipCodePattern"
        (inputChange)="zipInput$.next($event)"
        [attr.aria-invalid]="isValidationShouldBeShown(this.form.controls.zip) ? 'true' : null"
        [attr.aria-describedby]="isValidationShouldBeShown(this.form.controls.zip) ? errorIds.zip : null"
        [attr.aria-required]="true"
      >
        @let zipInput = zipInput$ | async;
        @let zipCodes = zipCodes$ | async;
        @if (zipInput && zipInput.length < zipMinInput) {
          <div class="text-gray-750 p-6 text-center text-sm">Please enter at least {{ zipMinInput }} symbols</div>
        } @else if (zipInput && !isZipLoading && (!zipCodes || !zipCodes.length)) {
          <div class="text-gray-750 p-6 text-center text-sm" data-testid="autocomplete-no-results">
            Sorry, we couldn’t find matching results
          </div>
        }
        @for (item of zipCodes; track $index) {
          <div
            [cslOption]="item"
            [innerHTML]="suggestionDisplayFn(item) | bold: zipInput"
            data-testid="autocomplete-option"
          ></div>
        }
      </csl-autocomplete>
      @if (isValidationShouldBeShown(this.form.controls.zip)) {
        <csl-validation-messages
          class="ml-1"
          [errors]="this.form.controls.zip.errors"
          [attr.id]="errorIds.zip"
          aria-live="polite"
        >
          <csl-validation-message name="required-address"> {{ errorMessages.zip }} </csl-validation-message>
        </csl-validation-messages>
      }
    </div>
    <div
      class="basis-7/12"
      [ngClass]="{ 'g-control-invalid group': isValidationShouldBeShown(this.form.controls.state) }"
      e2e-render-type="dropdown"
      e2e-question-code="state"
    >
      <label for="state" cslLabel required class="mx-2 mb-1" id="state-label">State</label>
      <csl-select
        id="state"
        placeholder="Select State"
        formControlName="state"
        displayValue="option"
        aria-labelledby="state-label"
        [attr.aria-invalid]="isValidationShouldBeShown(this.form.controls.state) ? 'true' : null"
        [attr.aria-describedby]="isValidationShouldBeShown(this.form.controls.state) ? errorIds.state : null"
        [attr.aria-required]="true"
      >
        <div [cslOption]="null" [hidden]="true"></div>
        @for (item of states; track $index) {
          <div [cslOption]="item.abbreviation">{{ stateDisplayFn(item) }}</div>
        }
      </csl-select>
      @if (isValidationShouldBeShown(this.form.controls.state)) {
        <csl-validation-messages
          class="ml-1"
          [errors]="this.form.controls.state.errors"
          [attr.id]="errorIds.state"
          aria-live="polite"
        >
          <csl-validation-message name="required"> {{ errorMessages.state }} </csl-validation-message>
        </csl-validation-messages>
      }
    </div>
  </div>
</form>
